import React, { useEffect, useRef, useState } from 'react';
import {
  Badge, Button, Card, Checkbox, Empty, Form, Input, message, Modal, Progress,
  Pagination, Select, Space, Tag, Tooltip, Typography,
} from 'antd';
import {
  CloudDownloadOutlined, DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined,
  EyeOutlined, PlusOutlined, ReloadOutlined, RocketOutlined, SearchOutlined,
  CloudUploadOutlined, DeploymentUnitOutlined, ShareAltOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import VersionShareLinkModal from '../components/VersionShareLinkModal';
import {
  deleteVersion,
  downloadVersionFile,
  downloadGitLabVersionFile,
  getGitLabRelease,
  getPublishJob,
  getVersionDetail,
  getProducts,
  getVersions,
  prepublishVersion,
  publishVersion,
  uploadPrepublishedVersion,
  scanVersions,
} from '../services/api';
import { StatusBadge, ReleaseTypeBadge } from '../components/StatusBadge';
import {
  formatFileSize, GITLAB_PRODUCTS, getProductDisplayName, isMainVersionProductVisible,
} from '../utils/format';

const { Text, Title } = Typography;

const NON_DELETABLE_PRODUCT_MODELS = new Set(['Welzek_WaveBox', 'T6290_SCPI_Manual']);
const DAILY_PUBLISHABLE_PRODUCT_MODELS = new Set(['T6290E', 'T6290ES', 'T6290F', 'T8290A', 'WelzekViewer']);

function isVersionDeleteAllowed(record) {
  return record?.version_type !== 'daily'
    && record?.version_type !== 'market'
    && !NON_DELETABLE_PRODUCT_MODELS.has(record?.product_model);
}

function isDailyVersionPublishAllowed(record) {
  return record?.version_type === 'daily'
    && DAILY_PUBLISHABLE_PRODUCT_MODELS.has(record?.product_model);
}

const PAGE_META = {
  release: {
    title: '发布版本',
    subtitle: '查看已发布固件与软件版本，支持下载、GitLab Release 链接和删除管理。',
    color: '#52c41a',
  },
  daily: {
    title: '每日版本',
    subtitle: '查看 CI 每日构建、测试状态和发布入口。',
    color: '#faad14',
  },
  market: {
    title: '市场版本',
    subtitle: '管理 X:\\4_市场版本 下的客户定制版本与市场资料，支持扫描、上传和下载。',
    color: '#1677ff',
  },
  all: {
    title: '版本列表',
    subtitle: '统一管理发布版本、每日构建和市场版本，支持筛选、扫描、发布与下载。',
  },
};

const PUBLISH_STAGE_LABELS = {
  queued: '等待执行',
  checking: '检查版本',
  copying: '写入系统路径',
  gitlab: '上传 GitLab',
  notifying: '发送飞书通知',
  done: '发布完成',
};

const PUBLISH_STAGE_PROGRESS = {
  queued: 5,
  checking: 15,
  copying: 45,
  gitlab: 75,
  notifying: 90,
  done: 100,
};

export default function VersionList() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releaseVersions, setReleaseVersions] = useState([]);
  const [dailyVersions, setDailyVersions] = useState([]);
  const [marketVersions, setMarketVersions] = useState([]);
  const [releasePagination, setReleasePagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [dailyPagination, setDailyPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [marketPagination, setMarketPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({ product_id: undefined, search: '' });
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [publishRecord, setPublishRecord] = useState(null);
  const [publishMode, setPublishMode] = useState('publish');
  const [publishLoading, setPublishLoading] = useState(false);
  const [welzekViewerDailyOptions, setWelzekViewerDailyOptions] = useState([]);
  const [welzekViewerDailyLoading, setWelzekViewerDailyLoading] = useState(false);
  const [publishJobModalOpen, setPublishJobModalOpen] = useState(false);
  const [publishJob, setPublishJob] = useState(null);
  const [uploadingPrepublishId, setUploadingPrepublishId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [shareRecord, setShareRecord] = useState(null);

  const publishFormRef = useRef(null);
  const deleteFormRef = useRef(null);
  const skipNextFetchRef = useRef(false);
  const scanIntervalRef = useRef(null);
  const publishJobPollRef = useRef(null);
  const publishPollTimeoutWarnedRef = useRef(false);
  const publishRetryRef = useRef(null);
  const uploadPrepublishNotifyRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin' || user?.is_super_admin;
  const isReleaseOnly = location.pathname === '/versions/release';
  const isDailyOnly = location.pathname === '/versions/daily';
  const isMarketOnly = location.pathname === '/versions/market';
  const isSingleMode = isReleaseOnly || isDailyOnly || isMarketOnly;
  const currentMode = isReleaseOnly ? 'release' : isDailyOnly ? 'daily' : isMarketOnly ? 'market' : 'all';
  const currentMeta = PAGE_META[currentMode];

  const normalizeSearch = (val) => (val ? val.trim().replace(/^[vV]+/, '') : val);
  const filterMainVersionRecords = (records) => (
    (records || []).filter(record => isMainVersionProductVisible({ model: record.product_model }))
  );

  const getActivePageSize = () => {
    if (isMarketOnly) return marketPagination.pageSize;
    if (isDailyOnly) return dailyPagination.pageSize;
    return releasePagination.pageSize;
  };

  const fetchAll = (pageRel = 1, pageDaily = 1, pageSize = 10, overrideFilters = null, options = {}) => {
    setLoading(true);
    const { silentErrors = false } = options;
    const currentFilters = overrideFilters || filters;
    const cleanSearch = normalizeSearch(currentFilters.search);

    if (isMarketOnly) {
      getVersions({
        search: cleanSearch,
        version_type: 'market',
        page: pageRel,
        pageSize,
      }).then((res) => {
        setMarketVersions(res.data.data);
        setMarketPagination({ current: res.data.page, pageSize: res.data.pageSize, total: res.data.total });
        setLoading(false);
      }).catch((err) => {
        setLoading(false);
        if (!silentErrors) {
          message.error(err.response?.data?.message || '搜索失败');
        }
      });
      return;
    }

    const paramsRel = {
      ...currentFilters,
      search: cleanSearch,
      version_type: 'release',
      page: pageRel,
      pageSize,
    };
    const paramsDaily = {
      ...currentFilters,
      search: cleanSearch,
      version_type: 'daily',
      page: pageDaily,
      pageSize,
    };

    const promises = [];
    if (!isDailyOnly) promises.push(getVersions(paramsRel));
    if (!isReleaseOnly) promises.push(getVersions(paramsDaily));

    Promise.all(promises).then((results) => {
      let relIdx = 0;
      let dailyIdx = 1;
      if (isReleaseOnly) {
        dailyIdx = -1;
      }
      if (isDailyOnly) {
        relIdx = -1;
        dailyIdx = 0;
      }

      if (relIdx >= 0) {
        const resRel = results[relIdx];
        setReleaseVersions(filterMainVersionRecords(resRel.data.data));
        setReleasePagination({ current: resRel.data.page, pageSize: resRel.data.pageSize, total: resRel.data.total });
      }
      if (dailyIdx >= 0) {
        const resDaily = results[dailyIdx];
        setDailyVersions(filterMainVersionRecords(resDaily.data.data));
        setDailyPagination({ current: resDaily.data.page, pageSize: resDaily.data.pageSize, total: resDaily.data.total });
      }
      setLoading(false);
    }).catch((err) => {
      setLoading(false);
      if (!silentErrors) {
        message.error(err.response?.data?.message || '搜索失败');
      }
    });
  };

  useEffect(() => {
    getProducts().then(res => setProducts(res.data.data));
    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      if (publishJobPollRef.current) {
        clearInterval(publishJobPollRef.current);
        publishJobPollRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productId = params.get('product_id');
    const nextFilters = {
      product_id: productId && !isMarketOnly ? Number(productId) : undefined,
      search: filters.search,
    };
    setFilters(nextFilters);
    fetchAll(1, 1, getActivePageSize(), nextFilters);
    skipNextFetchRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchAll(1, 1, getActivePageSize());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, location.pathname]);

  const handleSearch = () => {
    fetchAll(1, 1, getActivePageSize());
  };

  const handleRefresh = () => {
    if (scanning) return;
    setScanning(true);
    setReleasePagination(p => ({ ...p, current: 1 }));
    setDailyPagination(p => ({ ...p, current: 1 }));
    setMarketPagination(p => ({ ...p, current: 1 }));
    fetchAll(1, 1, getActivePageSize());

    const getScanStartError = (err, label) => {
      if (!err.response) return `${label}扫描状态暂时无法确认，页面会继续自动刷新`;
      return `${label}扫描启动失败：${err.response?.data?.message || err.message || '请求失败'}`;
    };

    scanVersions().then((versionRes) => {
      const successMsgs = [];
      const errorMsgs = [];
      const uncertainMsgs = [];
      if (versionRes.data?.success) successMsgs.push('版本扫描已开始');
      if (successMsgs.length > 0) message.success(successMsgs.join('，'));
      if (uncertainMsgs.length > 0) message.warning(uncertainMsgs.join('，'));
      if (errorMsgs.length > 0) message.error(errorMsgs.join('，'));
    }).catch((err) => {
      const msg = getScanStartError(err, '版本');
      if (err.response) message.error(msg);
      else message.warning(msg);
    });

    let count = 0;
    scanIntervalRef.current = setInterval(() => {
      count += 1;
      fetchAll(1, 1, getActivePageSize(), null, { silentErrors: true });
      if (count >= 20) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
        setScanning(false);
        message.info('自动刷新已结束，如需查看最新数据请再次点击刷新');
      }
    }, 3000);
  };

  const handleDelete = (record) => {
    if (record.version_type === 'market') {
      message.warning('市场版本由产品归档记录同步，不允许在页面删除');
      return;
    }
    if (!isVersionDeleteAllowed(record)) {
      message.warning(`${getProductDisplayName(record.product_model) || '该版本'} 不允许在页面删除`);
      return;
    }
    setDeleteRecord(record);
    setDeleteModalOpen(true);
    setTimeout(() => deleteFormRef.current?.resetFields(), 0);
  };

  const handleDeleteSubmit = async () => {
    const values = await deleteFormRef.current?.validateFields();
    if (!deleteRecord) return;
    setDeleteLoading(true);
    try {
      const data = {
        version: deleteRecord.version,
        version_type: deleteRecord.version_type,
        status: deleteRecord.status,
        product_model: deleteRecord.product_model,
        source_version: deleteRecord.source_version,
      };
      if (values.deleteFile) data.deleteFile = true;
      if (values.deleteGitLab) data.deleteGitLab = true;
      await deleteVersion(deleteRecord.id, data);
      message.success('删除成功');
      setDeleteModalOpen(false);
      setDeleteRecord(null);
      fetchAll(releasePagination.current, dailyPagination.current, getActivePageSize());
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    } finally {
      setDeleteLoading(false);
    }
  };

  const loadWelzekViewerDailyOptions = async () => {
    setWelzekViewerDailyLoading(true);
    setWelzekViewerDailyOptions([]);
    try {
      let productList = products;
      if (!productList.length) {
        const productRes = await getProducts();
        productList = productRes.data?.data || [];
        setProducts(productList);
      }
      const viewerProduct = productList.find(product => product.model === 'WelzekViewer');
      if (!viewerProduct) throw new Error('未找到 WelzekViewer 产品');

      const versionRes = await getVersions({
        product_id: viewerProduct.id,
        version_type: 'daily',
        page: 1,
        pageSize: 10,
      });
      setWelzekViewerDailyOptions((versionRes.data?.data || []).map(item => ({
        label: `v${item.version}（${item.file_name || '压缩包'}）`,
        value: item.id,
      })));
    } catch (err) {
      message.warning(err.response?.data?.message || err.message || 'WelzekViewer 每日版本加载失败');
    } finally {
      setWelzekViewerDailyLoading(false);
    }
  };

  const handlePublish = (record, mode = 'publish') => {
    setPublishRecord(record);
    setPublishMode(mode);
    setPublishModalOpen(true);
    if (record.product_model === 'T6290E') {
      loadWelzekViewerDailyOptions();
    } else {
      setWelzekViewerDailyOptions([]);
    }
    setTimeout(() => {
      publishFormRef.current?.setFieldsValue({
        version: record.version,
        welzekViewerDailyVersionId: undefined,
        releaseAnnouncementDescription: '',
        notifyReleaseAnnouncement: mode === 'publish',
      });
    }, 0);
  };

  const stopPublishJobPolling = () => {
    if (publishJobPollRef.current) {
      clearInterval(publishJobPollRef.current);
      publishJobPollRef.current = null;
    }
  };

  const resetPublishFlow = () => {
    setPublishRecord(null);
    setPublishMode('publish');
    setPublishLoading(false);
    publishPollTimeoutWarnedRef.current = false;
    publishRetryRef.current = null;
    setWelzekViewerDailyOptions([]);
    setWelzekViewerDailyLoading(false);
    publishFormRef.current?.resetFields();
  };

  const getPublishJobProgress = (job) => {
    if (!job) return 0;
    if (job.status === 'success') return 100;
    if (job.status === 'failed') return PUBLISH_STAGE_PROGRESS[job.stage] || 20;
    return PUBLISH_STAGE_PROGRESS[job.stage] || 10;
  };

  const showReleaseNoteVersionInfoMissingModal = (errorMessage, onOk) => {
    Modal.error({
      title: '版本信息缺少',
      content: (
        <div style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
          {errorMessage || 'ReleaseNote版本信息缺少，无法发布'}
        </div>
      ),
      onOk,
    });
  };

  const showPublishWarningsModal = (warnings, onOk) => {
    Modal.warning({
      title: 'T6290E 发布完成，但 WelzekViewer 未复制',
      content: (
        <div>
          <div style={{ marginBottom: 8 }}>T6290E 已正常完成，本次附带文件处理存在以下提醒：</div>
          <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
            {warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
          </ul>
        </div>
      ),
      width: 620,
      onOk,
    });
  };

  const handlePublishJobUpdate = (job) => {
    if (!job) return;
    setPublishJob(job);

    if (job.status === 'success') {
      stopPublishJobPolling();
      setPublishJobModalOpen(false);
      message.success(job.result?.message || '发布成功');
      const releaseId = job.release_id || job.result?.id || job.result?.releaseId;
      const warnings = Array.isArray(job.result?.warnings) ? job.result.warnings : [];
      fetchAll(releasePagination.current, dailyPagination.current, getActivePageSize());
      resetPublishFlow();
      const navigateToRelease = () => {
        if (releaseId) navigate(`/versions/${releaseId}`);
      };
      if (warnings.length > 0) {
        showPublishWarningsModal(warnings, navigateToRelease);
      } else {
        navigateToRelease();
      }
      return;
    }

    if (job.status === 'failed') {
      stopPublishJobPolling();
      setPublishLoading(false);
      setPublishJobModalOpen(false);
      if (job.error_code === 'OUTDATED_FILES') {
        Modal.confirm({
          title: '文件日期警告',
          icon: <ExclamationCircleOutlined />,
          content: (
            <div style={{ maxHeight: 400, overflow: 'auto' }}>
              <div style={{ marginBottom: 12, color: '#ff4d4f', fontWeight: 600 }}>
                以下文件的修改日期早于上一个发布版本，是否仍要发布？
              </div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
                {job.error_message}
              </div>
            </div>
          ),
          okText: '仍然发布',
          okType: 'danger',
          cancelText: '取消',
          width: 640,
          onOk: () => publishRetryRef.current?.(true),
          onCancel: resetPublishFlow,
        });
        return;
      }
      if (job.error_code === 'RELEASE_NOTE_VERSION_INFO_MISSING') {
        showReleaseNoteVersionInfoMissingModal(job.error_message, resetPublishFlow);
        return;
      }
      Modal.error({
        title: '发布失败',
        content: (
          <div style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
            {job.error_message || '发布失败'}
          </div>
        ),
        onOk: resetPublishFlow,
      });
    }
  };

  const isPublishPollTimeout = (err) => (
    err?.code === 'ECONNABORTED'
    || err?.code === 'ETIMEDOUT'
    || /timeout/i.test(err?.message || '')
  );

  const pollPublishJob = async (jobId) => {
    try {
      const res = await getPublishJob(jobId);
      handlePublishJobUpdate(res.data?.data);
    } catch (err) {
      if (isPublishPollTimeout(err)) {
        if (!publishPollTimeoutWarnedRef.current) {
          message.warning('发布状态查询超时，系统将继续等待发布结果');
          publishPollTimeoutWarnedRef.current = true;
        }
        return;
      }
      stopPublishJobPolling();
      setPublishLoading(false);
      setPublishJobModalOpen(false);
      Modal.error({
        title: '发布状态查询失败',
        content: err.response?.data?.message || err.message || '发布状态查询失败',
        onOk: resetPublishFlow,
      });
    }
  };

  const startPublishJobPolling = (jobId) => {
    stopPublishJobPolling();
    publishPollTimeoutWarnedRef.current = false;
    pollPublishJob(jobId);
    publishJobPollRef.current = setInterval(() => {
      pollPublishJob(jobId);
    }, 2000);
  };

  const handlePublishSubmit = async () => {
    const values = await publishFormRef.current?.validateFields();
    if (!publishRecord) return;

    const attemptPublish = async (force = false) => {
      setPublishLoading(true);
      try {
        if (publishMode === 'prepublish') {
          const res = await prepublishVersion(publishRecord.id, {
            version: values.version,
            force,
            welzekViewerDailyVersionId: values.welzekViewerDailyVersionId || null,
            notifyReleaseAnnouncement: false,
          });
          message.success(res.data?.message || '预发布成功');
          const releaseId = res.data?.data?.id || publishRecord.id;
          const warnings = Array.isArray(res.data?.data?.warnings) ? res.data.data.warnings : [];
          setPublishModalOpen(false);
          resetPublishFlow();
          if (warnings.length > 0) {
            showPublishWarningsModal(warnings, () => navigate(`/versions/${releaseId}`));
          } else {
            navigate(`/versions/${releaseId}`);
          }
          return;
        }

        const res = await publishVersion(publishRecord.id, {
          version: values.version,
          force,
          welzekViewerDailyVersionId: values.welzekViewerDailyVersionId || null,
          notifyReleaseAnnouncement: values.notifyReleaseAnnouncement !== false,
          releaseAnnouncementDescription: values.releaseAnnouncementDescription || '',
        });
        const jobId = res.data?.data?.jobId;
        const job = res.data?.data?.job;
        if (!jobId) {
          throw new Error('发布任务创建失败');
        }
        setPublishModalOpen(false);
        setPublishJob(job || { id: jobId, status: 'queued', stage: 'queued' });
        setPublishJobModalOpen(true);
        message.success(res.data?.message || '发布任务已开始');
        if (job) handlePublishJobUpdate(job);
        if (!job || !['success', 'failed'].includes(job.status)) {
          startPublishJobPolling(jobId);
        }
      } catch (err) {
        setPublishLoading(false);
        if (err.response?.data?.code === 'OUTDATED_FILES') {
          Modal.confirm({
            title: '文件日期警告',
            icon: <ExclamationCircleOutlined />,
            content: (
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                <div style={{ marginBottom: 12, color: '#ff4d4f', fontWeight: 600 }}>
                  以下文件的修改日期早于上一个发布版本，是否仍要发布？
                </div>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6 }}>
                  {err.response.data.message}
                </div>
              </div>
            ),
            okText: publishMode === 'prepublish' ? '仍然预发布' : '仍然发布',
            okType: 'danger',
            cancelText: '取消',
            width: 640,
            onOk: () => attemptPublish(true),
          });
        } else if (err.response?.data?.code === 'RELEASE_NOTE_VERSION_INFO_MISSING') {
          showReleaseNoteVersionInfoMissingModal(
            err.response?.data?.message,
            () => {
              setPublishModalOpen(false);
              resetPublishFlow();
            }
          );
        } else {
          Modal.error({
            title: '发布失败',
            content: (
              <div style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
                {err.response?.data?.message || err.message || '发布失败'}
              </div>
            ),
          });
        }
      } finally {
        if (publishMode === 'prepublish') {
          setPublishLoading(false);
        }
      }
    };

    publishRetryRef.current = attemptPublish;
    attemptPublish();
  };

  const handleUploadPrepublish = (record) => {
    uploadPrepublishNotifyRef.current = true;
    Modal.confirm({
      title: '上传预发布版本到 GitLab?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <Space direction="vertical" size={12}>
          <Text>
            将使用系统路径下已经生成的预发布版本创建 GitLab Release，成功后该版本会转为正式发布。
          </Text>
          <Checkbox
            defaultChecked
            onChange={(e) => { uploadPrepublishNotifyRef.current = e.target.checked; }}
          >
            发布成功后同步通知飞书群
          </Checkbox>
        </Space>
      ),
      okText: '上传到 GitLab',
      cancelText: '取消',
      onOk: async () => {
        setUploadingPrepublishId(record.id);
        try {
          const res = await uploadPrepublishedVersion(record.id, {
            notifyReleaseAnnouncement: uploadPrepublishNotifyRef.current,
          });
          message.success(res.data?.message || '已上传到 GitLab');
          fetchAll(releasePagination.current, dailyPagination.current, getActivePageSize());
          navigate(`/versions/${res.data?.data?.id || record.id}`);
        } catch (err) {
          Modal.error({
            title: '上传 GitLab 失败',
            content: err.response?.data?.message || err.message || '上传 GitLab 失败',
          });
        } finally {
          setUploadingPrepublishId(null);
        }
      },
    });
  };

  const confirmDownloadWithRemarks = async (record, downloadTask) => {
    const runDownloadTask = async () => {
      try {
        await downloadTask();
      } catch (err) {
        message.error(err.response?.data?.message || err.message || '下载失败');
      }
    };

    const hasRemarkForDownload = Number(record.remark_count || 0) > 0
      || Boolean(String(record.latest_remark_content || '').trim());
    if (!hasRemarkForDownload) {
      await runDownloadTask();
      return;
    }

    let remarks = [];
    try {
      const res = await getVersionDetail(record.id);
      remarks = (res.data?.data?.remarks || [])
        .filter((remark) => String(remark.content || '').trim());
    } catch (err) {
      message.error(err.response?.data?.message || '获取版本备注失败');
      return;
    }

    if (remarks.length === 0) {
      await runDownloadTask();
      return;
    }

    Modal.confirm({
      title: `版本备注 - ${getProductDisplayName(record.product_model)} v${record.version}`,
      icon: <ExclamationCircleOutlined />,
      width: 640,
      okText: '继续下载',
      cancelText: '取消',
      content: (
        <div className="download-remark-modal">
          <Text type="secondary">此版本有 {remarks.length} 条备注，请确认后继续下载。</Text>
          <div className="download-remark-list">
            {remarks.map((remark, index) => (
              <div className="download-remark-item" key={remark.id || `${remark.created_at}-${index}`}>
                <div className="download-remark-meta">
                  <Text strong>{remark.created_by_name || remark.created_by_email || '未知用户'}</Text>
                  <Text type="secondary">{dayjs(remark.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                </div>
                <div className="download-remark-content">{remark.content}</div>
              </div>
            ))}
          </div>
        </div>
      ),
      onOk: runDownloadTask,
    });
  };

  const handleGitLabDownload = async (record) => {
    await confirmDownloadWithRemarks(record, async () => {
      const res = await getGitLabRelease(record.id);
      if (res.data.data.assets.length === 0) {
        message.warning('GitLab Release 中没有可下载的文件');
        return;
      }
      await downloadGitLabVersionFile(record.id);
    });
  };

  const handleLocalDownload = async (record) => {
    await confirmDownloadWithRemarks(record, () => downloadVersionFile(record.id));
  };

  const renderActionButtons = (record) => {
    const hasPrepublishedRelease = Number(record.pre_release_count || 0) > 0;
    const canPublishDailyVersion = isDailyVersionPublishAllowed(record);
    const canShareRecord = record.version_type === 'release'
      && record.status === 'released'
      && ((record.file_path && record.file_name) || GITLAB_PRODUCTS.includes(record.product_model));
    const preReleaseDeleteRecord = record.version_type === 'daily' && record.pre_release_id ? {
      ...record,
      id: record.pre_release_id,
      version: record.pre_release_version || record.version,
      version_type: 'release',
      status: 'pre_released',
      file_path: record.pre_release_file_path,
      file_name: record.pre_release_file_name,
      source_version: record.version,
    } : null;
    const canUploadPrepublish = record.status === 'pre_released'
      || (record.version_type === 'daily' && hasPrepublishedRelease);

    return (
    <Space size={4} wrap>
      <Tooltip title="查看详情">
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/versions/${record.id}`)} />
      </Tooltip>
      {record.file_path && record.file_name ? (
        <Tooltip title="下载文件">
          <Button type="link" size="small" icon={<DownloadOutlined />} onClick={() => handleLocalDownload(record)} />
        </Tooltip>
      ) : null}
      {record.status === 'released' && GITLAB_PRODUCTS.includes(record.product_model) ? (
        <Tooltip title="从 GitLab 下载">
          <Button type="link" size="small" icon={<CloudDownloadOutlined />} onClick={() => handleGitLabDownload(record)} />
        </Tooltip>
      ) : null}
      {canShareRecord ? (
        <Tooltip title="分享下载链接">
          <Button type="link" size="small" icon={<ShareAltOutlined />} onClick={() => setShareRecord(record)} />
        </Tooltip>
      ) : null}
      {isAdmin && canPublishDailyVersion ? (
        <Tooltip title="发布">
          <Button type="link" size="small" icon={<RocketOutlined />} onClick={() => handlePublish(record, 'publish')} />
        </Tooltip>
      ) : null}
      {isAdmin && canPublishDailyVersion ? (
        <Tooltip title="预发布">
          <Button type="link" size="small" icon={<DeploymentUnitOutlined />} onClick={() => handlePublish(record, 'prepublish')} />
        </Tooltip>
      ) : null}
      {isAdmin && (record.version_type === 'daily' || record.status === 'pre_released') && GITLAB_PRODUCTS.includes(record.product_model) ? (
        <Tooltip title={canUploadPrepublish ? '上传预发布到 GitLab' : '请先预发布'}>
          <Button
            type="link"
            size="small"
            icon={<CloudUploadOutlined />}
            loading={uploadingPrepublishId === record.id}
            disabled={!canUploadPrepublish}
            onClick={() => handleUploadPrepublish(record)}
          />
        </Tooltip>
      ) : null}
      {isAdmin && preReleaseDeleteRecord ? (
        <Tooltip title="删除预发布">
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(preReleaseDeleteRecord)} />
        </Tooltip>
      ) : null}
      {isAdmin && record.version_type !== 'daily' && record.version_type !== 'market' && isVersionDeleteAllowed(record) ? (
        <Tooltip title="删除">
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Tooltip>
      ) : null}
    </Space>
    );
  };

  const getMarketInfo = (record) => {
    if (record.version_type !== 'market' || !record.version_info) return {};
    try {
      return JSON.parse(record.version_info).market || {};
    } catch (err) {
      return {};
    }
  };

  function renderVersionCardRemark(record, isMarketRecord) {
    const remarkContent = String(record.latest_remark_content || '').trim();
    if (remarkContent) {
      return (
        <div className="daily-version-remark">
          <span className="daily-version-remark-label">备注</span>
          <Text type="secondary" className="daily-version-remark-content">
            {remarkContent}
          </Text>
          {Number(record.remark_count) > 1 && (
            <Tag className="daily-version-remark-count">{record.remark_count} 条</Tag>
          )}
        </div>
      );
    }

    if (isMarketRecord || record.version_type !== 'release') {
      return (
        <Text type="secondary" className="daily-version-description">
          {record.description || record.file_name || '暂无描述'}
        </Text>
      );
    }

    if (!remarkContent) {
      return null;
    }
  }

  const renderVersionCards = (versions, pagination, onChange, titleBadgeColor, titleText) => (
    <Card
      className="content-card"
      title={
        isSingleMode ? undefined : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Badge color={titleBadgeColor} />
            <Text strong style={{ fontSize: 16 }}>{titleText}</Text>
          </div>
        )
      }
      style={{ marginBottom: isSingleMode ? 0 : 16 }}
      loading={loading || scanning}
    >
      {versions.length === 0 ? (
        <Empty description={`暂无${titleText}`} />
      ) : (
        <div className="daily-version-list">
          {versions.map((record) => {
            const marketInfo = getMarketInfo(record);
            const isMarketRecord = record.version_type === 'market';
            const displayVersionNumber = isMarketRecord ? record.version : `v${record.version}`;
            return (
              <Card
                key={record.id}
                className="daily-version-card"
                size="small"
                hoverable
                onDoubleClick={() => navigate(`/versions/${record.id}`)}
              >
                <div className="daily-version-main">
                  <div className="daily-version-title">
                    <Text strong className="daily-version-product">
                      {isMarketRecord ? (marketInfo.productModel || '市场版本') : getProductDisplayName(record.product_model)}
                    </Text>
                    <Text strong className="daily-version-number">{displayVersionNumber}</Text>
                  </div>
                  <Space size={6} wrap>
                    {!isMarketRecord && <ReleaseTypeBadge type={record.release_type} />}
                    {(record.version_type !== 'daily' || ['pre_released', 'released'].includes(record.status)) && (
                      <StatusBadge status={record.status} />
                    )}
                    {isMarketRecord && <Tag color="blue">市场归档</Tag>}
                  </Space>
                  {renderVersionCardRemark(record, isMarketRecord)}
                </div>

                <div className="daily-version-meta">
                  {isMarketRecord ? (
                    <>
                      <div>
                        <span>客户</span>
                        <strong>{marketInfo.customer || '-'}</strong>
                      </div>
                      <div>
                        <span>FAE</span>
                        <strong>{marketInfo.fae || '-'}</strong>
                      </div>
                      <div>
                        <span>归档日期</span>
                        <strong>{marketInfo.archiveDate || dayjs(record.created_at).format('YYYY-MM-DD')}</strong>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span>文件大小</span>
                        <strong>{formatFileSize(record.file_size)}</strong>
                      </div>
                      <div>
                        <span>上传者</span>
                        <strong>{record.uploaded_by || '-'}</strong>
                      </div>
                      <div>
                        <span>上传时间</span>
                        <strong>{dayjs(record.created_at).format('YYYY-MM-DD HH:mm')}</strong>
                      </div>
                    </>
                  )}
                </div>

                <div className="daily-version-actions">
                  {renderActionButtons(record)}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="daily-version-pagination">
        <Pagination
          {...pagination}
          showSizeChanger
          showTotal={(total) => `共 ${total} 个${titleText}`}
          onChange={(page, pageSize) => onChange(page, pageSize)}
        />
      </div>
    </Card>
  );

  return (
    <div className="page-shell">
      <VersionShareLinkModal
        open={Boolean(shareRecord)}
        version={shareRecord}
        onClose={() => setShareRecord(null)}
      />
      <div className="page-heading">
        <div>
          <div className="page-kicker">Firmware Library</div>
          <Title level={3} className="page-heading-title">{currentMeta.title}</Title>
          <Text className="page-heading-subtitle">{currentMeta.subtitle}</Text>
        </div>
        <Space wrap>
          <Tag color="#1f9d55" style={{ margin: 0 }}>Release</Tag>
          <Tag color="#ff7f0d" style={{ margin: 0 }}>Daily</Tag>
          <Tag color="#1677ff" style={{ margin: 0 }}>Market</Tag>
        </Space>
      </div>

      <Card className="toolbar-card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Space wrap>
            {!isMarketOnly && (
              <Select
                placeholder="选择产品"
                allowClear
                style={{ width: 200 }}
                value={filters.product_id}
                onChange={v => setFilters({ ...filters, product_id: v })}
                options={products.filter(isMainVersionProductVisible).map(p => ({ label: getProductDisplayName(p.model), value: p.id }))}
              />
            )}
            <Input
              placeholder="搜索版本号..."
              prefix={<SearchOutlined />}
              allowClear
              style={{ width: 200 }}
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              onPressEnter={handleSearch}
            />
            <Button onClick={handleSearch}>搜索</Button>
          </Space>
          <Space wrap>
            <Button icon={<ReloadOutlined />} loading={scanning} onClick={handleRefresh}>
              刷新
            </Button>
            {isAdmin && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/upload')}>
                上传版本
              </Button>
            )}
          </Space>
        </div>
      </Card>

      {isMarketOnly && renderVersionCards(
        marketVersions,
        marketPagination,
        (page, pageSize) => fetchAll(page, 1, pageSize),
        PAGE_META.market.color,
        '市场版本'
      )}

      {!isDailyOnly && !isMarketOnly && renderVersionCards(
        releaseVersions,
        releasePagination,
        (page, pageSize) => fetchAll(page, dailyPagination.current, pageSize),
        PAGE_META.release.color,
        '发布版本'
      )}

      {!isReleaseOnly && !isMarketOnly && renderVersionCards(
        dailyVersions,
        dailyPagination,
        (page, pageSize) => fetchAll(releasePagination.current, page, pageSize),
        PAGE_META.daily.color,
        '每日版本'
      )}

      <Modal
        title={`${publishMode === 'prepublish' ? '预发布' : '发布'} ${getProductDisplayName(publishRecord?.product_model) || ''} 版本`}
        open={publishModalOpen}
        onOk={handlePublishSubmit}
        onCancel={() => {
          setPublishModalOpen(false);
          setPublishRecord(null);
          setPublishMode('publish');
          setWelzekViewerDailyOptions([]);
          setWelzekViewerDailyLoading(false);
          publishFormRef.current?.resetFields();
        }}
        confirmLoading={publishLoading}
        okText={publishMode === 'prepublish' ? '确认预发布' : '确认发布'}
        cancelText="取消"
      >
        <Form ref={publishFormRef} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="版本号"
            name="version"
            rules={[{ required: true, message: '请输入版本号' }]}
          >
            <Input placeholder="请输入版本号" />
          </Form.Item>
          {publishRecord?.product_model === 'T6290E' && (
            <Form.Item
              label="附带 WelzekViewer 每日版本（可选）"
              name="welzekViewerDailyVersionId"
              extra="保持原文件名和内容，复制到 T6290E 发布版本目录；不选择不影响发布。"
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                loading={welzekViewerDailyLoading}
                placeholder="请选择最近 10 个 WelzekViewer 每日版本之一"
                options={welzekViewerDailyOptions}
                notFoundContent={welzekViewerDailyLoading ? '正在加载...' : '暂无可选每日版本'}
              />
            </Form.Item>
          )}
          {publishMode === 'publish' && (
            <>
              <Form.Item
                label="版本描述"
                name="releaseAnnouncementDescription"
              >
                <Input.TextArea
                  placeholder="请输入发布到飞书的版本描述，未填写默认为无"
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  maxLength={500}
                  showCount
                  allowClear
                />
              </Form.Item>
              <Form.Item
                name="notifyReleaseAnnouncement"
                valuePropName="checked"
                initialValue={true}
              >
                <Checkbox>发布成功后同步通知飞书群</Checkbox>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title="发布任务"
        open={publishJobModalOpen}
        footer={[
          <Button
            key="close"
            disabled={publishJob && !['success', 'failed'].includes(publishJob.status)}
            onClick={() => setPublishJobModalOpen(false)}
          >
            关闭
          </Button>,
        ]}
        closable={publishJob && ['success', 'failed'].includes(publishJob.status)}
        maskClosable={false}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Progress
            percent={getPublishJobProgress(publishJob)}
            status={publishJob?.status === 'failed' ? 'exception' : 'active'}
          />
          <Text strong>
            {PUBLISH_STAGE_LABELS[publishJob?.stage] || '发布中'}
          </Text>
          <Text type="secondary">
            {publishJob?.status === 'failed'
              ? publishJob.error_message || '发布失败'
              : '页面可以保持打开，系统会自动刷新发布状态。'}
          </Text>
        </Space>
      </Modal>

      <Modal
        title={
          <span>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
            确认删除版本 v{deleteRecord?.version}?
          </span>
        }
        open={deleteModalOpen}
        onOk={handleDeleteSubmit}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteRecord(null);
        }}
        confirmLoading={deleteLoading}
        okText="确认删除"
        okButtonProps={{ danger: true }}
        cancelText="取消"
      >
        <Form ref={deleteFormRef} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item>
            <Text type="secondary">
              此操作将删除 {getProductDisplayName(deleteRecord?.product_model)} v{deleteRecord?.version} 及其所有关联数据（变更记录、备注等）。
            </Text>
          </Form.Item>
          {deleteRecord?.file_path && (deleteRecord.file_path.startsWith('X:') || deleteRecord.file_path.startsWith('x:')) && (
            <Form.Item name="deleteFile" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>同时删除系统路径下的版本文件</Checkbox>
            </Form.Item>
          )}
          {deleteRecord?.status === 'released' && GITLAB_PRODUCTS.includes(deleteRecord?.product_model) && (
            <Form.Item name="deleteGitLab" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>同时删除 GitLab 上的 Release</Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
